// ── Shared useTTS hook ───────────────────────────────────────────────────────
// Public API:
//   const { speak, pause, play, stop, seek, download, wasCached, status, error,
//           hasAudio, audioComplete, progress } = useTTS({ onError });
//
// status: 'idle' | 'loading' | 'playing' | 'paused' | 'error'
//
// speak() resolves true once that request's whole clip has arrived, false if
//   it failed, was stopped, or was replaced by a newer speak(). Anything that
//   charges for a generation should wait for this, not count the click.
//
// wasCached(): whether the latest speak() was answered from Cache Storage, and
//   so never reached Modal. Such a clip was not saved to History.
//
// error: a sentence for the visitor (see ERROR_MESSAGES in ttsClient), never
//   the raw server or browser message; that one goes to the console.
//
// onError (optional): called with the cause — a TTSError, or whatever else
//   was thrown — each time `error` is set, so it can be logged. An abort is
//   not a failure and never reaches it.
//
// hasAudio:      some audio for the latest request has arrived.
// audioComplete: all of it has, so download() has the whole clip. False while
//   a stream is still arriving, and after one that failed or was stopped
//   partway. Stopping after it completed leaves it true: the clip is cached.
//
// progress: { current, duration, buffering, durationKnown, chunksLoaded }
//   - current        seconds elapsed across the whole (multi-chunk) timeline
//   - duration       seconds currently loaded/known (grows while streaming)
//   - buffering      true while more audio chunks are still incoming, from the
//                    moment the request goes out
//   - durationKnown  true once the stream is finished AND every chunk's
//                    duration has been measured (i.e. `duration` is final)
//   - chunksLoaded   number of audio chunks received so far
//
// Modal path (USE_MODAL=true):
//   - Streams chunks from /synthesize_stream and plays each WAV sequentially.
//   - Chunk blobs are retained for the whole session so the timeline is fully
//     seekable (backward and forward into already-loaded audio).
//   - Persists chunks in Cache Storage. Identical (text, speed, voice) requests
//     replay from cache without a network round-trip.
//   - Mobile-Safari user-gesture rule: a tiny silent WAV is play()'d
//     synchronously inside the click handler to "unlock" subsequent playback.
//
// SageMaker path (USE_MODAL=false):
//   - Single fetch → blob → Audio element. Natively seekable.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  USE_MODAL,
  requestTTS,
  streamTTS,
  ttsCacheKey,
  cfgSignature,
  getCachedAudio,
  putCachedChunk,
  putCachedManifest,
  TTSError,
  errorMessage,
} from './ttsClient';

// Merge multiple WAV blobs into a single playable WAV.
// Each Modal chunk arrives as its own RIFF/WAVE file, so we keep the first
// fmt chunk and concatenate all data chunks under a single header.
const mergeWavBlobs = async blobs => {
  if (!blobs.length) return null;
  if (blobs.length === 1) return blobs[0];
  const buffers = await Promise.all(blobs.map(b => b.arrayBuffer()));
  let fmtChunk = null;
  const dataChunks = [];
  const readId = (dv, off) =>
    String.fromCharCode(
      dv.getUint8(off),
      dv.getUint8(off + 1),
      dv.getUint8(off + 2),
      dv.getUint8(off + 3)
    );
  for (const buf of buffers) {
    const dv = new DataView(buf);
    if (readId(dv, 0) !== 'RIFF' || readId(dv, 8) !== 'WAVE') continue;
    let off = 12;
    while (off + 8 <= dv.byteLength) {
      const id = readId(dv, off);
      const size = dv.getUint32(off + 4, true);
      if (id === 'fmt ' && !fmtChunk) {
        fmtChunk = new Uint8Array(buf, off, 8 + size);
      } else if (id === 'data') {
        dataChunks.push(new Uint8Array(buf, off + 8, size));
      }
      off += 8 + size + (size % 2);
    }
  }
  if (!fmtChunk || !dataChunks.length) return blobs[0];
  const dataLen = dataChunks.reduce((s, c) => s + c.length, 0);
  const total = 12 + fmtChunk.length + 8 + dataLen;
  const out = new Uint8Array(total);
  const dv = new DataView(out.buffer);
  const writeId = (off, s) => {
    for (let i = 0; i < 4; i++) out[off + i] = s.charCodeAt(i);
  };
  writeId(0, 'RIFF');
  dv.setUint32(4, total - 8, true);
  writeId(8, 'WAVE');
  out.set(fmtChunk, 12);
  let p = 12 + fmtChunk.length;
  writeId(p, 'data');
  dv.setUint32(p + 4, dataLen, true);
  p += 8;
  for (const c of dataChunks) {
    out.set(c, p);
    p += c.length;
  }
  return new Blob([out], { type: 'audio/wav' });
};

const WAV_FILE = {
  description: 'WAV audio',
  accept: { 'audio/wav': ['.wav'] },
};

// The browser's own Save As window, where there is one: Chrome and Edge on a
// computer. Must run soon after the click, or the browser refuses to open it.
const saveWithPicker = async (blob, filename) => {
  const handle = await window.showSaveFilePicker({
    suggestedName: filename,
    types: [WAV_FILE],
  });
  const file = await handle.createWritable();
  await file.write(blob);
  await file.close();
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Resolves false if the reader cancelled Save As, true otherwise. Anywhere
// without the window, or if it refuses to open, the browser downloads as normal.
const saveBlob = async (blob, filename) => {
  if (window.showSaveFilePicker) {
    try {
      await saveWithPicker(blob, filename);
      return true;
    } catch (err) {
      if (err.name === 'AbortError') return false;
    }
  }
  downloadBlob(blob, filename);
  return true;
};

// ~10ms of mono 8-bit silence at 8kHz — enough to satisfy iOS Safari's
// "first play() must be inside a user gesture" rule.
const SILENT_WAV =
  'data:audio/wav;base64,UklGRkAAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YRwAAAB/f39/f39/f39/f39/f39/f39/f39/f39/f39/';

const EMPTY_PROGRESS = {
  current: 0,
  duration: 0,
  buffering: false,
  durationKnown: false,
  chunksLoaded: 0,
};

const DEBUG = process.env.REACT_APP_LOCAL_ENV === 'true';
const log = (...a) => {
  if (DEBUG) console.log(...a);
};

// The page only shows ERROR_MESSAGES, so this is the one place in the browser
// the real cause of a failed request surfaces. Logged in every environment, not
// just DEBUG: it is what a visitor's bug report can point at.
const reportFailure = err => {
  console.error('[TTS] Generation failed:', err);
};

// A clip the browser refused to play. MediaError's code says why (2 network,
// 3 decode, 4 unsupported source).
const playbackError = audio =>
  new TTSError(
    'playback',
    `MediaError ${audio.error?.code ?? '?'}: ${audio.error?.message ?? ''}`
  );

const useTTS = ({ onError } = {}) => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [hasAudio, setHasAudio] = useState(false);
  const [audioComplete, setAudioComplete] = useState(false);
  const [progress, setProgress] = useState(EMPTY_PROGRESS);

  // The api below is built once, so it reaches the caller's latest onError
  // through a ref rather than the one the first render passed.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Every failure the visitor is shown goes through here: the sentence for
  // the page, the cause for the console and for onError.
  const fail = err => {
    reportFailure(err);
    setError(errorMessage(err));
    onErrorRef.current?.(err);
  };

  // Per-hook mutable controller (created once).
  const ctlRef = useRef(null);
  if (ctlRef.current === null) {
    ctlRef.current = {
      // Common
      sessionId: 0,
      abort: null,

      // Streaming (Modal)
      chunks: [], // Array<{ url, blob, duration:number|null }> retained for session
      playIndex: 0, // index of currently playing / next chunk
      currentAudio: null, // HTMLAudioElement of chunk in flight
      pendingSeekOffset: 0, // intra-chunk offset (s) to apply when next chunk starts
      networkDone: true,
      userPaused: false,
      lastHash: null, // hash of last successful (or in-progress) request
      complete: false, // mirrors `audioComplete` for the []-deps API below
      fromCache: false, // the latest request was answered from Cache Storage

      // Single-blob (SageMaker)
      blobAudio: null,
      blobKey: null,
    };
  }
  const ctl = ctlRef.current;

  const markComplete = complete => {
    ctl.complete = complete;
    setAudioComplete(complete);
  };

  // ── Progress publishing ───────────────────────────────────────────────────
  const publishProgress = () => {
    if (!USE_MODAL) {
      const a = ctl.blobAudio;
      const dur = a && isFinite(a.duration) ? a.duration : 0;
      setProgress({
        current: a ? a.currentTime || 0 : 0,
        duration: dur,
        buffering: false,
        durationKnown: dur > 0,
        chunksLoaded: a ? 1 : 0,
      });
      return;
    }

    let duration = 0;
    let allKnown = true;
    for (const c of ctl.chunks) {
      if (c.duration == null || !isFinite(c.duration)) allKnown = false;
      else duration += c.duration;
    }
    let current = 0;
    for (let i = 0; i < ctl.playIndex && i < ctl.chunks.length; i++) {
      current += ctl.chunks[i].duration || 0;
    }
    current += ctl.currentAudio
      ? ctl.currentAudio.currentTime || 0
      : ctl.pendingSeekOffset || 0;

    setProgress({
      current,
      duration,
      buffering: !ctl.networkDone,
      durationKnown: allKnown && ctl.networkDone,
      chunksLoaded: ctl.chunks.length,
    });
  };

  // ── Streaming helpers ─────────────────────────────────────────────────────
  // Detach + pause the live chunk element. Does NOT revoke its object URL —
  // chunk URLs live in ctl.chunks and are released together in releaseChunks().
  const stopCurrentAudio = () => {
    const a = ctl.currentAudio;
    if (a) {
      a.onended = null;
      a.onpause = null;
      a.onplay = null;
      a.onerror = null;
      a.ontimeupdate = null;
      a.onloadedmetadata = null;
      try {
        a.pause();
      } catch {
        /* noop */
      }
    }
    ctl.currentAudio = null;
  };

  // Free every retained chunk and reset the timeline. Used on stop / new speak.
  const releaseChunks = () => {
    ctl.chunks.forEach(c => {
      try {
        URL.revokeObjectURL(c.url);
      } catch {
        /* noop */
      }
    });
    ctl.chunks = [];
    ctl.playIndex = 0;
    ctl.pendingSeekOffset = 0;
  };

  const playChunk = sessionId => {
    if (sessionId !== ctl.sessionId) return;
    if (ctl.userPaused) return;
    if (ctl.currentAudio) return; // already playing a chunk

    const item = ctl.chunks[ctl.playIndex];
    if (!item) {
      // Nothing ready at this index. If the network is also done we've reached
      // the true end; otherwise wait for the next chunk to enqueue.
      if (ctl.networkDone && ctl.playIndex >= ctl.chunks.length) {
        setStatus(s => (s === 'playing' || s === 'loading' ? 'idle' : s));
      }
      return;
    }

    const audio = new Audio(item.url);
    ctl.currentAudio = audio;
    const offset = ctl.pendingSeekOffset || 0;

    audio.onloadedmetadata = () => {
      item.duration = audio.duration;
      if (offset > 0) {
        try {
          audio.currentTime = Math.min(offset, audio.duration || offset);
        } catch {
          /* noop */
        }
      }
      ctl.pendingSeekOffset = 0;
      publishProgress();
    };
    audio.onplay = () => {
      if (sessionId === ctl.sessionId) setStatus('playing');
    };
    audio.ontimeupdate = () => {
      if (sessionId === ctl.sessionId) publishProgress();
    };
    audio.onpause = () => {
      // User-initiated pause; end-of-track also fires pause but onended runs after.
      if (audio.ended) return;
      if (sessionId === ctl.sessionId && ctl.userPaused) {
        setStatus('paused');
      }
    };
    audio.onended = () => {
      item.duration = audio.duration;
      ctl.currentAudio = null;
      ctl.playIndex += 1;
      publishProgress();
      playChunk(sessionId);
    };
    audio.onerror = () => {
      if (sessionId !== ctl.sessionId) return;
      setStatus('error');
      fail(playbackError(audio));
    };
    audio.play().catch(() => {
      /* gesture race; user can hit Play */
    });
  };

  // Measure a chunk's duration off the audio path so the full timeline length
  // is known shortly after a chunk arrives (enables accurate seeking).
  const probeDuration = (item, sessionId) => {
    const probe = new Audio();
    probe.preload = 'metadata';
    probe.src = item.url;
    probe.onloadedmetadata = () => {
      if (item.duration == null && isFinite(probe.duration)) {
        item.duration = probe.duration;
        if (sessionId === ctl.sessionId) publishProgress();
      }
    };
  };

  const enqueue = (blob, sessionId) => {
    if (sessionId !== ctl.sessionId) return;
    const url = URL.createObjectURL(blob);
    const item = { url, blob, duration: null };
    ctl.chunks.push(item);
    probeDuration(item, sessionId);
    setHasAudio(true);
    publishProgress();
    playChunk(sessionId);
  };

  // ── Streaming speak ───────────────────────────────────────────────────────
  const speakStreaming = async ({
    text,
    speed,
    voice,
    cfg,
    historyToken,
    sessionId,
    force,
  }) => {
    setStatus('loading');
    setError(null);
    markComplete(false);
    ctl.fromCache = false;
    releaseChunks();
    stopCurrentAudio();
    // Buffering from the start, not from the first chunk: the wait for that
    // chunk is the longest of the request (a cold model can take half a
    // minute), and an empty bar with nothing moving reads as a hang.
    setProgress({ ...EMPTY_PROGRESS, buffering: true });
    ctl.userPaused = false;
    ctl.networkDone = false;

    // Cache lookup first (skipped on force=true so Regenerate hits the network).
    const hash = await ttsCacheKey(text, speed, voice, cfg);
    if (sessionId !== ctl.sessionId) return;
    ctl.lastHash = hash;

    if (!force) {
      const cached = await getCachedAudio(hash);
      if (sessionId !== ctl.sessionId) return;
      if (cached && cached.length > 0) {
        ctl.networkDone = true;
        ctl.fromCache = true;
        cached.forEach(blob => enqueue(blob, sessionId));
        markComplete(true);
        return;
      }
    }

    // Cache miss — hit the network.
    const ctrl = new AbortController();
    ctl.abort = ctrl;
    try {
      const total = await streamTTS({
        text,
        speed,
        voice,
        cfg,
        historyToken,
        signal: ctrl.signal,
        onChunk: async (blob, idx) => {
          if (sessionId !== ctl.sessionId) return;
          await putCachedChunk(hash, idx, blob);
          enqueue(blob, sessionId);
        },
      });
      if (sessionId !== ctl.sessionId) return;
      ctl.networkDone = true;
      markComplete(true);
      await putCachedManifest(hash, total);
      publishProgress();
      // Nudge state if everything already drained.
      if (!ctl.currentAudio && ctl.playIndex >= ctl.chunks.length) {
        setStatus(s => (s === 'loading' ? 'idle' : s));
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      if (sessionId !== ctl.sessionId) return;
      ctl.networkDone = true;
      publishProgress();
      fail(e);
      // Let any already-queued chunks finish playing; only flip to 'error'
      // if there's nothing left to play.
      if (!ctl.currentAudio && ctl.playIndex >= ctl.chunks.length) {
        setStatus('error');
      }
    }
  };

  // ── Single-blob speak (SageMaker) ─────────────────────────────────────────
  const wireBlobAudio = (audio, sessionId) => {
    audio.onplay = () => {
      if (sessionId === ctl.sessionId) setStatus('playing');
    };
    audio.onloadedmetadata = () => {
      if (sessionId === ctl.sessionId) publishProgress();
    };
    audio.ontimeupdate = () => {
      if (sessionId === ctl.sessionId) publishProgress();
    };
    audio.onpause = () => {
      if (!audio.ended) {
        setStatus(s => (s === 'playing' ? 'paused' : s));
      }
    };
    audio.onended = () => {
      if (sessionId === ctl.sessionId) {
        setStatus('idle');
        publishProgress();
      }
    };
    audio.onerror = () => {
      setStatus('error');
      fail(playbackError(audio));
    };
  };

  const speakBlob = async ({ text, speed, voice, cfg, sessionId, force }) => {
    const key = `${voice}|${speed}|${cfgSignature(cfg)}|${text}`;
    if (!force && ctl.blobAudio && ctl.blobKey === key) {
      try {
        ctl.blobAudio.currentTime = 0;
        await ctl.blobAudio.play();
      } catch {
        /* ignore */
      }
      return;
    }

    if (ctl.blobAudio) {
      try {
        ctl.blobAudio.pause();
      } catch {
        /* noop */
      }
    }
    setProgress(EMPTY_PROGRESS);
    markComplete(false);
    const ctrl = new AbortController();
    ctl.abort = ctrl;
    setStatus('loading');
    setError(null);

    try {
      const { src } = await requestTTS({
        text,
        speed,
        voice,
        cfg,
        signal: ctrl.signal,
      });
      if (sessionId !== ctl.sessionId) return;
      const audio = new Audio(src);
      ctl.blobAudio = audio;
      ctl.blobKey = key;
      setHasAudio(true);
      // One response is the whole clip.
      markComplete(true);
      wireBlobAudio(audio, sessionId);
      await audio.play();
    } catch (e) {
      if (e.name === 'AbortError') return;
      fail(e);
      setStatus('error');
    }
  };

  // ── Public API (stable identities via ref-bouncing useMemo) ───────────────
  const api = useMemo(() => {
    return {
      // Async only for its result: the body has no await, so the Safari
      // primer below still runs synchronously inside the click.
      speak: async (text, opts = {}) => {
        const {
          speed = 1.0,
          voice = 'Default',
          force = false,
          // Optional model guidance knobs, keyed by API name; any knob left
          // out is left to the backend's own default.
          cfg,
          // A signed-in reader's access token, so Modal saves the finished
          // clip to History. Streaming only, and never part of the cache key.
          historyToken,
        } = opts;
        if (!text || !text.trim()) return false;
        if (!USE_MODAL && text.length > 500) {
          fail(new TTSError('tooLong', `${text.length} characters`));
          setStatus('error');
          return false;
        }

        // Bump session — invalidates any in-flight callbacks from previous speak().
        ctl.sessionId += 1;
        const sessionId = ctl.sessionId;
        if (ctl.abort) {
          try {
            ctl.abort.abort();
          } catch {
            /* noop */
          }
          ctl.abort = null;
        }

        // SAFARI USER-GESTURE PRIMER:
        // We must call .play() on a real Audio element synchronously inside
        // the click event chain, *before* any awaits, so iOS unlocks audio
        // for this session. The silent WAV plays for ~10ms, inaudible.
        try {
          const primer = new Audio(SILENT_WAV);
          // intentionally not awaited — fire and forget within the gesture
          primer.play().catch(() => {});
          // pause shortly after so it doesn't tie up the audio channel
          setTimeout(() => {
            try {
              primer.pause();
            } catch {
              /* noop */
            }
          }, 50);
        } catch {
          /* primer is best-effort */
        }

        const run = USE_MODAL
          ? speakStreaming({
              text,
              speed,
              voice,
              cfg,
              historyToken,
              sessionId,
              force,
            })
          : speakBlob({ text, speed, voice, cfg, sessionId, force });
        // Still this session's request, and its whole clip arrived. A newer
        // speak() or a Stop bumps the session, so a request they cut off
        // resolves false even if its last chunk lands afterwards.
        return run.then(() => sessionId === ctl.sessionId && ctl.complete);
      },

      pause: () => {
        ctl.userPaused = true;
        const a = ctl.currentAudio ?? ctl.blobAudio;
        if (a && !a.paused && !a.ended) {
          try {
            a.pause();
          } catch {
            /* noop */
          }
        }
        setStatus(s => (s === 'playing' ? 'paused' : s));
      },

      play: () => {
        ctl.userPaused = false;

        // Mid-chunk resume.
        if (ctl.currentAudio) {
          ctl.currentAudio.play().catch(() => {});
          return;
        }

        // Streaming: timeline still has chunks left to play (paused / mid-stream).
        if (USE_MODAL && ctl.playIndex < ctl.chunks.length) {
          playChunk(ctl.sessionId);
          return;
        }

        // Streaming: finished but chunks retained → replay from the start.
        if (USE_MODAL && ctl.chunks.length > 0) {
          ctl.playIndex = 0;
          ctl.pendingSeekOffset = 0;
          publishProgress();
          playChunk(ctl.sessionId);
          return;
        }

        // Streaming: nothing live but cache exists → replay from cache.
        if (USE_MODAL && ctl.lastHash) {
          ctl.sessionId += 1;
          const sessionId = ctl.sessionId;
          (async () => {
            const cached = await getCachedAudio(ctl.lastHash);
            if (sessionId !== ctl.sessionId || !cached || !cached.length)
              return;
            releaseChunks();
            stopCurrentAudio();
            ctl.networkDone = true;
            ctl.userPaused = false;
            cached.forEach(blob => enqueue(blob, sessionId));
          })();
          return;
        }

        // SageMaker: just resume / restart the cached blob.
        if (ctl.blobAudio) {
          ctl.blobAudio.play().catch(() => {});
        }
      },

      wasCached: () => ctl.fromCache,

      // Seek to an absolute time (seconds) on the unified timeline.
      seek: timeSec => {
        const target = Math.max(0, timeSec);

        if (!USE_MODAL) {
          const a = ctl.blobAudio;
          if (!a) return;
          try {
            a.currentTime = isFinite(a.duration)
              ? Math.min(target, a.duration)
              : target;
          } catch {
            /* noop */
          }
          publishProgress();
          return;
        }

        if (!ctl.chunks.length) return;

        // Locate the chunk that contains `target`.
        let acc = 0;
        let idx = 0;
        for (; idx < ctl.chunks.length; idx++) {
          const d = ctl.chunks[idx].duration || 0;
          if (acc + d > target || idx === ctl.chunks.length - 1) break;
          acc += d;
        }
        const offset = Math.max(0, target - acc);

        // Resume playing after the seek unless the user has explicitly paused.
        // (ctl is the source of truth here — the `status` state is a stale
        // closure inside this []-deps useMemo.)
        const wasPlaying = !ctl.userPaused;
        stopCurrentAudio();
        ctl.playIndex = idx;
        ctl.pendingSeekOffset = offset;

        if (wasPlaying) {
          // Resume playback from the new position immediately.
          playChunk(ctl.sessionId);
        } else {
          // Stay paused; reflect the new scrub position in the UI.
          publishProgress();
        }
      },

      stop: () => {
        ctl.sessionId += 1;
        if (ctl.abort) {
          try {
            ctl.abort.abort();
          } catch {
            /* noop */
          }
          ctl.abort = null;
        }
        releaseChunks();
        stopCurrentAudio();
        if (ctl.blobAudio) {
          try {
            ctl.blobAudio.pause();
            ctl.blobAudio.currentTime = 0;
          } catch {
            /* noop */
          }
        }
        ctl.userPaused = false;
        ctl.networkDone = true;
        setStatus('idle');
        setProgress(EMPTY_PROGRESS);
      },

      download: async (filename = 'shona-tts.wav') => {
        if (!ctl.complete) return false;
        if (USE_MODAL) {
          // The retained chunks are the whole clip unless Stop released them,
          // and then the cache still has it. Reading memory first also keeps
          // downloads working where Cache Storage is unavailable.
          const blobs = ctl.chunks.length
            ? ctl.chunks.map(c => c.blob)
            : await getCachedAudio(ctl.lastHash);
          if (!blobs || !blobs.length) return false;
          const merged = await mergeWavBlobs(blobs);
          if (!merged) return false;
          return saveBlob(merged, filename);
        }
        const src = ctl.blobAudio?.src;
        if (!src) return false;
        try {
          const res = await fetch(src);
          const blob = await res.blob();
          return await saveBlob(blob, filename);
        } catch {
          return false;
        }
      },
    };
  }, []);

  return { ...api, status, error, hasAudio, audioComplete, progress };
};

export default useTTS;
